// JSON-LD for the pages whose prerendered block and React block must be the
// same object — ONE implementation, imported by the prerender
// (scripts/prerender-body.mjs, scripts/prerender-og.mjs) and by the React pages.
//
// WHY: the prerendered JSON-LD sits inside #prerender-body, which src/main.tsx
// removes before React mounts, so what the page emits at run time is what a
// rendering crawler (Google) indexes. Written separately, the two drifted: /ideas
// and 18 info pages lost every block on hydration, the homepage had schema only
// after hydration, and category pages changed name, description and breadcrumb.
//
// Plain data builders: no Node APIs, so Vite can bundle this file. Keep
// scripts/routes.mjs out of its imports — the homepage (entry bundle) reads it.

export const SITE = "https://loftloom.com";

/** A same-origin path as an absolute URL; anything else passes through. */
export const absoluteUrl = (u) => (u && String(u).startsWith("/") ? `${SITE}${u}` : u);

/** `trail` is [{ name, url }], from Home down to the page itself. */
export function breadcrumbSchema(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({ "@type": "ListItem", position: i + 1, name: t.name, item: t.url })),
  };
}

// ---------------------------------------------------------------------------
// Homepage
// ---------------------------------------------------------------------------

// The Organization and WebSite objects, and the list of the two, live in
// scripts/site-identity.mjs (src/components/JsonLd.tsx reads them too);
// re-exported here so every page's schema list comes through this module.
export { homeSchemas } from "./site-identity.mjs";

// ---------------------------------------------------------------------------
// Flat routes (scripts/routes.mjs): Home > page
// ---------------------------------------------------------------------------

/** Crumb names that are not simply the first part of the route's <title>. */
const STATIC_CRUMB_NAMES = {
  "/ideas": "Journal",
  "/categories": "All Categories",
  "/faq": "FAQ",
  "/shipping": "Shipping",
  "/returns": "Returns",
  "/get-the-look": "Get the Look",
};

/** `route` is a STATIC_ROUTES row. */
export const staticCrumbName = (route) => STATIC_CRUMB_NAMES[route.path] ?? route.title.split(/\s+[—|]\s+/)[0];

/**
 * The BreadcrumbList of a flat route, or null for the routes that have none of
 * this shape: the homepage, and the sizing tools, which emit their own
 * three-level one (scripts/tools-content.mjs).
 */
export function staticBreadcrumbSchema(route) {
  if (!route || route.path === "/" || route.path === "/tools" || route.path.startsWith("/tools/")) return null;
  return breadcrumbSchema([
    { name: "Home", url: `${SITE}/` },
    { name: staticCrumbName(route), url: `${SITE}${route.path}` },
  ]);
}

/** /ideas: every published guide. `articles` are JournalArticleMeta rows. */
export function ideasItemListSchema(articles) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "LoftLoom Journal",
    numberOfItems: articles.length,
    itemListElement: articles.map((a, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE}/ideas/${a.slug}`, name: a.title })),
  };
}

// ---------------------------------------------------------------------------
// Guides (/ideas/<slug>)
// ---------------------------------------------------------------------------

/**
 * A markdown FAQ answer as plain text for JSON-LD: links keep their label,
 * emphasis and list marks go. The schema used to carry the raw markdown
 * ("**diagonal depth**", "[rug size guide](/ideas/rug-size-guide)").
 */
const plainAnswer = (md) =>
  String(md ?? "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/(^|[^\w*])\*([^*\n]+)\*(?=[^\w*]|$)/g, "$1$2")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, "")
    .replace(/\s*\n+\s*/g, " ")
    .trim();

/**
 * Article + BreadcrumbList (+ FAQPage). `faq` is [{ q, a }] parsed from the
 * body; `lastmod` is the guide's entry in scripts/data/journal-lastmod.json,
 * `firstCommit` its entry in scripts/data/journal-first-commit.json.
 *
 * Dates are real or absent, never guessed: datePublished is the guide's
 * publishedDate or, for the guides published before that field was kept, the
 * date of the commit that first carried their text; dateModified is the date
 * of the commit that last changed it. A few guides were published the day
 * after their last recorded change, so dateModified is never earlier than
 * datePublished.
 */
export function articleSchemas(article, { faq = [], lastmod, firstCommit } = {}) {
  const canonical = `${SITE}/ideas/${article.slug}`;
  const published = article.publishedDate || firstCommit || undefined;
  const modified = [lastmod, published].filter(Boolean).sort().pop();
  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.description,
      // Absolute: a relative URL in JSON-LD is resolved by nobody.
      image: article.hero ? [absoluteUrl(article.hero)] : undefined,
      datePublished: published,
      dateModified: modified,
      author: { "@type": "Organization", name: "LoftLoom" },
      publisher: {
        "@type": "Organization",
        name: "LoftLoom",
        logo: { "@type": "ImageObject", url: `${SITE}/images/loftloom-logo.png` },
      },
      mainEntityOfPage: canonical,
    },
    breadcrumbSchema([
      { name: "Home", url: SITE },
      { name: "Journal", url: `${SITE}/ideas` },
      { name: article.title, url: canonical },
    ]),
  ];
  if (faq.length && /FAQPage/i.test(String(article.schema))) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: plainAnswer(f.a) },
      })),
    });
  }
  return schemas;
}

// ---------------------------------------------------------------------------
// Categories (/category/<slug>)
// ---------------------------------------------------------------------------

/**
 * CollectionPage + BreadcrumbList.
 * `parent` is the category's parent row, if any — the same single level the
 * page's visible breadcrumb shows. `items` are the products on the page (only
 * whether there are any is read); `total` is the build-time in-stock count
 * (src/data/categoryStock.ts). A category with nothing to list gets the
 * breadcrumb only: an empty CollectionPage describes nothing.
 *
 * The products themselves are not listed. The served page took them from the
 * build cache and the hydrated page from the live, weighted first page, so
 * the two blocks named different products on 36 of 119 categories, and the
 * live list changes whenever the supplier's stock does. The page body links
 * every product it shows.
 *
 * numberOfItems is that build-time count, the same on both pages. The
 * categories whose count is unknown at build time (-1: all, new-arrivals, the
 * price bands …) carry no ItemList at all.
 */
export function categorySchemas(category, { parent = null, items = [], total = 0 } = {}) {
  const url = `${SITE}/category/${category.slug}`;
  const crumbs = breadcrumbSchema([
    { name: "Home", url: `${SITE}/` },
    ...(parent ? [{ name: parent.name, url: `${SITE}/category/${parent.slug}` }] : []),
    { name: category.name, url },
  ]);
  if (!items.length) return [crumbs];
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: category.name,
      description: category.description,
      url,
      isPartOf: { "@type": "WebSite", name: "LoftLoom", url: SITE },
      // undefined is dropped by JSON.stringify.
      mainEntity: Number(total) > 0 ? { "@type": "ItemList", numberOfItems: Number(total) } : undefined,
    },
    crumbs,
  ];
}
